import React from 'react';
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	ExtensionManifest,
	ExtensionModule,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';
import type { QuickInsertPreview } from '@atlaskit/editor-common/quick-insert/preview';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import DefaultExtensionProvider from '@atlaskit/editor-common/extensions/default-extension-provider';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { editorCommandToPMCommand } from '@atlaskit/editor-common/preset/editor-commands';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { showLinkToolbar } from '@atlaskit/editor-plugin-hyperlink/commands';
import VideoIcon from '@atlaskit/icon/core/video';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import {
	getSetEmbedIntent,
	getSetPreferredTabKey,
} from '../components/RovoLinkPickerTabPreferenceContext.tsx';

// Lazily load the shared app-icon module. It eagerly pulls in ~60 third-party
// SVG icon components plus @atlaskit/logo, so a static import drags that whole
// graph into the editor's initial-load bundle for every edit-page session.
// Quick-insert icons only ever render once the slash/insert menu is opened, so
// resolving the module inside the async `icon` loader keeps it off the
// initial-load (VC) critical path.
const loadAppIcon = async () =>
	(
		await import(
			/* webpackChunkName: "loadable-search-ai-app-icon" */ '@atlassian/search-ai/app-icon'
		)
	).AppIcon;

const loadEmbedIcon = async () =>
	(
		await import(
			/* webpackChunkName: "loadable-embed-quick-insert-icon" */ '@atlaskit/icon/core/link'
		)
	).default;

const URL_BASED_APP_ICONS: Readonly<Record<string, { size: number; url: string }>> = {
	'google-drive': {
		size: 32,
		url: '/gateway/api/assist/rovo/v1/resources/icon/google_drive',
	},
	figma: {
		size: 32,
		url: 'https://dam-cdn.atl.orangelogic.com/AssetLink/68t3023r7347271te7qw370k2pk37rgb.svg',
	},
	amplitude: {
		size: 32,
		url: 'https://dam-cdn.atl.orangelogic.com/AssetLink/730r6bie0211kg6obni4343y4g1wv6n5.svg',
	},
};

const i18n = defineMessages({
	appLinkDescription: {
		id: 'editor-features.third-party-quick-insert.app-link.description',
		defaultMessage: 'Insert a link from {appName}',
		description:
			'Description for a quick-insert (slash command) menu item that inserts a link from a third-party app. {appName} is the product name (e.g. Google Drive, Figma) and must not be translated.',
	},
	googleAttribution: {
		id: 'editor-features.third-party-quick-insert.preview-attribution.google',
		defaultMessage: 'Google',
		description: 'Google company name shown in a Quick Insert preview attribution.',
	},
	figmaAttribution: {
		id: 'editor-features.third-party-quick-insert.preview-attribution.figma',
		defaultMessage: 'Figma',
		description: 'Figma product name shown in a Quick Insert preview attribution.',
	},
	amplitudeAttribution: {
		id: 'editor-features.third-party-quick-insert.preview-attribution.amplitude',
		defaultMessage: 'Amplitude',
		description: 'Amplitude product name shown in a Quick Insert preview attribution.',
	},
	embedTitle: {
		id: 'editor-features.third-party-quick-insert.embed.title',
		defaultMessage: 'Embed',
		description:
			'Title for a quick-insert (slash command) menu item that opens the insert/link toolbar to embed external content.',
	},
	embedDescription: {
		id: 'editor-features.third-party-quick-insert.embed.description',
		defaultMessage: 'Insert a link or embed external content',
		description:
			'Description for a quick-insert (slash command) menu item that opens the insert/link toolbar to embed external content.',
	},
});

interface ThirdPartyAppConfig {
	tabKey: string;
	/** Product/brand name — intentionally not translated. */
	title: string;
	keywords: string[];
	appIconKey: string;
	preview?: {
		image?: QuickInsertPreview['image'];
		attribution: { name: MessageDescriptor };
	};
	category?: string;
	categories?: string[];
	priority?: number;
}

const THIRD_PARTY_APPS: ThirdPartyAppConfig[] = [
	{
		tabKey: 'google-drive',
		title: 'Google Drive',
		keywords: ['google', 'drive', 'gdrive', 'google drive', 'link', 'embed'],
		appIconKey: 'Google Drive',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKB.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNN.png',
			},
			attribution: { name: i18n.googleAttribution },
		},
		category: 'embed',
		categories: ['external-content'],
		priority: -700,
	},
	{
		tabKey: 'figma',
		title: 'Figma',
		keywords: ['figma', 'design', 'link', 'embed'],
		appIconKey: 'Figma',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVI7.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNB.png',
			},
			attribution: { name: i18n.figmaAttribution },
		},
		category: 'embed',
		categories: ['external-content'],
		priority: -600,
	},
	{
		tabKey: 'amplitude',
		title: 'Amplitude',
		keywords: ['amplitude', 'analytics', 'link', 'embed'],
		appIconKey: 'amplitude',
		preview: {
			image: {
				dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVHE.png',
				light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVKS.png',
			},
			attribution: { name: i18n.amplitudeAttribution },
		},
		category: 'embed',
		categories: ['external-content'],
		priority: -300,
	},
	{
		tabKey: 'loom',
		title: 'Loom',
		keywords: ['loom', 'video', 'recording', 'link', 'embed'],
		appIconKey: 'Loom',
		preview: {
			attribution: { name: quickInsertMessages.previewAttributionAtlassian },
		},
		category: 'media',
		categories: ['external-content'],
		priority: -1100,
	},
	{
		tabKey: 'sharepoint',
		title: 'SharePoint',
		keywords: ['sharepoint', 'microsoft', 'link', 'embed'],
		appIconKey: 'sharepoint',
		preview: {
			attribution: { name: quickInsertMessages.previewAttributionMicrosoft },
		},
		category: 'embed',
		categories: ['external-content'],
		priority: -400,
	},
];

/**
 * Maps a quick-insert `tabKey` to the product key used in the search config's
 * accessible/connected product lists. Most tab keys already match the config
 * key; only the cases that differ need an entry here. The Google Drive tab id is
 * `google-drive` (matching the link picker tab), but the search config exposes
 * it under the product key `drive`.
 */
const TAB_KEY_TO_CONFIG_PRODUCT_KEY: Record<string, string> = {
	'google-drive': 'drive',
};

const ALWAYS_VISIBLE_TAB_KEYS = new Set<string>(['loom']);

function isThirdPartyAppConnected(
	app: ThirdPartyAppConfig,
	connectedProductKeys: Set<string>,
): boolean {
	if (ALWAYS_VISIBLE_TAB_KEYS.has(app.tabKey)) {
		return true;
	}
	const configKey = (TAB_KEY_TO_CONFIG_PRODUCT_KEY[app.tabKey] ?? app.tabKey).toLowerCase();
	return connectedProductKeys.has(configKey);
}

interface CreateThirdPartyQuickInsertExtensionProviderOptions {
	editorActions: EditorActions;
	intlNext: IntlShape;
	cohort: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	/**
	 * Resolves with the set of connected third-party product keys once the
	 * connector-config request settles (or `undefined` to "fail open" and show
	 * every app). Passing a promise — rather than the already-resolved value —
	 * lets the provider be built exactly once with a stable identity: the editor
	 * keeps the same `extensionProviders` array and simply picks up the manifest
	 * when it resolves, instead of rebuilding every extension provider (and
	 * re-initialising the editor) when the value transitions from `undefined` to a
	 * `Set` partway through the editor's load.
	 */
	connectedProductKeysPromise?: Promise<Set<string> | undefined>;
}

export const createThirdPartyQuickInsertExtensionProvider = ({
	editorActions,
	intlNext,
	cohort,
	createAnalyticsEvent,
	connectedProductKeysPromise,
}: CreateThirdPartyQuickInsertExtensionProviderOptions): DefaultExtensionProvider<Parameters> => {
	// Fires when a user selects one of the quick-insert embed items (the generic
	// `/embed` item or a per-app item). Feeds the `/embed` usage-rate metric and,
	// via `itemType`/`appKey`, the breakdown of which action the user took (embed
	// vs a specific app name). Uses the Confluence analytics listener schema
	// (`sendUIEvent`) so the event is captured by the Confluence pipeline.
	const fireQuickInsertEmbedSelected = (attributes: {
		itemKey: string;
		itemType: 'embed' | 'thirdPartyApp';
		cohort: string;
		appKey?: string;
	}): void => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'selected',
				actionSubject: 'quickInsertItem',
				actionSubjectId: 'embedQuickInsert',
				source: 'editPageScreen',
				attributes,
			},
		}).fire();
	};

	const buildManifest = (connectedProductKeys?: Set<string>): ExtensionManifest => {
		const visibleApps = connectedProductKeys
			? THIRD_PARTY_APPS.filter((app) => isThirdPartyAppConnected(app, connectedProductKeys))
			: THIRD_PARTY_APPS;

		const quickInsertModules: ExtensionModule[] = visibleApps.map((app) => {
			const urlBasedAppIcon = URL_BASED_APP_ICONS[app.tabKey];
			const appModule: ExtensionModule = {
				key: `third-party-embed-${app.tabKey}`,
				title: app.title,
				description: intlNext.formatMessage(i18n.appLinkDescription, { appName: app.title }),
				keywords: app.keywords,
				category: isExperimentEnabled('platform_editor_slash_command') ? app.category : undefined,
				categories: ['external-content'],
				priority: isExperimentEnabled('platform_editor_slash_command') ? app.priority : undefined,
				...(app.preview
					? {
							preview: {
								image: app.preview.image,
								attribution: {
									name: intlNext.formatMessage(app.preview.attribution.name),
								},
							},
						}
					: {}),
				icon: async () => {
					if (urlBasedAppIcon && isExperimentEnabled('platform_editor_slash_command')) {
						return () => (
							<img
								src={urlBasedAppIcon.url}
								alt=""
								width={urlBasedAppIcon.size}
								height={urlBasedAppIcon.size}
							/>
						);
					}

					if (app.tabKey === 'loom' && isExperimentEnabled('platform_editor_slash_command')) {
						return () => <VideoIcon label="" />;
					}

					const AppIcon = await loadAppIcon();
					return () => <AppIcon app={app.appIconKey} size="small" />;
				},
				action: async () => {
					fireQuickInsertEmbedSelected({
						itemKey: `third-party-embed-${app.tabKey}`,
						itemType: 'thirdPartyApp',
						appKey: app.tabKey,
						cohort,
					});

					// Set the preferred tab key so the Rovo Link Picker opens on the correct tab
					getSetPreferredTabKey()(app.tabKey);
					// Record that this picker session was opened from a quick-insert embed entry
					// point so the adapter can request an embed appearance for the inserted link.
					getSetEmbedIntent()(true);

					// Defer the link toolbar open to let the type-ahead settle
					requestAnimationFrame(() => {
						const view = editorActions._privateGetEditorView();
						if (!view) {
							// The editor view went away before we could open the toolbar (e.g. the
							// editor unmounted between selection and this frame). The preference was
							// set synchronously above, but RovoLinkPickerAdapter will never mount to
							// clear it — so clear it here to avoid leaking a stale tab into the next
							// link picker open.
							getSetPreferredTabKey()(undefined);
							getSetEmbedIntent()(false);
							return;
						}
						editorCommandToPMCommand(showLinkToolbar(INPUT_METHOD.QUICK_INSERT, undefined))(
							view.state,
							view.dispatch,
						);
					});

					// Don't insert any ADF node
					return undefined;
				},
			};

			appModule.app = {
				key: app.tabKey,
				source: 'internal',
			};

			return appModule;
		});

		const embedModule: ExtensionModule = {
			key: 'embed',
			title: intlNext.formatMessage(i18n.embedTitle),
			description: intlNext.formatMessage(i18n.embedDescription),
			keywords: ['embed', 'link', 'url'],
			categories: ['external-content'],
			// Lower priority sorts earlier in the slash menu. Keep Embed after Amplitude
			// (-300) and before iframe (-200) in the registered slash-command ordering.
			// Outside that experiment, preserve the existing cohort-based behaviour.
			priority: isExperimentEnabled('platform_editor_slash_command')
				? -250
				: cohort === 'prioritizeEmbedInQIM'
					? -300
					: undefined,
			icon: loadEmbedIcon,
			preview: {
				attribution: {
					name: intlNext.formatMessage(quickInsertMessages.previewAttributionAtlassian),
				},
			},
			action: async () => {
				fireQuickInsertEmbedSelected({
					itemKey: 'embed',
					itemType: 'embed',
					cohort,
				});

				// Record that this picker session was opened from the quick-insert embed entry
				// point so the adapter can request an embed appearance for the inserted link.
				getSetEmbedIntent()(true);

				// Defer the link toolbar open to let the type-ahead settle
				requestAnimationFrame(() => {
					const view = editorActions._privateGetEditorView();
					if (!view) {
						// The editor view went away before we could open the toolbar. Clear the
						// embed intent so it can't leak into a later, unrelated link picker open.
						getSetEmbedIntent()(false);
						return;
					}
					editorCommandToPMCommand(showLinkToolbar(INPUT_METHOD.QUICK_INSERT, undefined))(
						view.state,
						view.dispatch,
					);
				});

				// Don't insert any ADF node
				return undefined;
			},
		};

		embedModule.app = {
			key: 'confluence',
			source: 'internal',
		};

		return {
			title: 'Third-party embeds',
			description: 'Quick-insert items for third-party app links',
			type: 'com.atlassian.confluence.macro.core',
			key: 'third-party-quick-insert-embeds',
			icons: {
				'48': async () => {
					const AppIcon = await loadAppIcon();
					return () => <AppIcon app={null} size="small" />;
				},
			},
			modules: {
				quickInsert: [embedModule, ...quickInsertModules],
			},
		};
	};

	// Build the provider once with a manifest promise so the editor's
	// `extensionProviders` array keeps a stable identity. When a connected-product
	// promise is supplied (experiment cohort) the manifest resolves after the
	// connector-config request settles; the editor picks up the quick-insert items
	// at that point without a prop change, so it is never re-initialised mid-load.
	// Without a promise we fail open and show every app synchronously.
	const manifestsPromise: Promise<ExtensionManifest[]> = connectedProductKeysPromise
		? connectedProductKeysPromise.then((connectedProductKeys) => [
				buildManifest(connectedProductKeys),
			])
		: Promise.resolve([buildManifest(undefined)]);

	return new DefaultExtensionProvider(manifestsPromise);
};
